// /borrower/StripeConnect.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Typography, Button, Spin, Alert, Space, Result } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';

import StripeOnboarding from './StripeOnboarding';
import { DataService } from '../../config/dataService/smipDataService';
import { handleApiError } from '../../utility/errorHandler';
import { Main } from '../../container/styled';
import { PageHeader } from '../page-headers/page-headers';

const { Title, Text, Paragraph } = Typography;

function BorrowerStripeConnect() {
  const [borrower, setBorrower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { borrowerId } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBorrowerDetails = async () => {
      if (!borrowerId) {
        setError('Borrower ID is not available');
        setLoading(false);
        return;
      }

      try {
        const response = await DataService.get(`/Borrowers?filter[id][_eq]=${borrowerId}`);
        console.log(response);

        if (response.data && response.data.length > 0) {
          const borrowerData = response.data[0];
          setBorrower(borrowerData);

          // Now fetch the user details if user_id exists
          if (borrowerData.user_id) {
            try {
              const userResponse = await DataService.get(`/Users?filter[id][_eq]=${borrowerData.user_id}`);
              if (userResponse.data && userResponse.data.length > 0) {
                // Update borrower with user email
                setBorrower({
                  ...borrowerData,
                  email: userResponse.data[0].email,
                });
              }
            } catch (userErr) {
              console.error('Failed to fetch user details:', userErr);
              // Continue with borrower data we have
            }
          }
        } else {
          setError('Borrower not found');
        }
      } catch (err) {
        handleApiError(err);
        setError('Failed to load borrower details');
      } finally {
        setLoading(false);
      }
    };

    fetchBorrowerDetails();
  }, [borrowerId]);

  const handleBackToDashboard = () => {
    navigate('/borrower/wallet/list');
  };

  if (loading) {
    return (
      <Main>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Loading borrower details...</Paragraph>
        </div>
      </Main>
    );
  }

  if (error) {
    return (
      <Main>
        <Card>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button type="primary" onClick={handleBackToDashboard}>
                Back to Dashboard
              </Button>
            }
          />
        </Card>
      </Main>
    );
  }

  const isStripeConnected = borrower?.stripe_account_id;

  return (
    <Main>
      <PageHeader
        title="Connect Stripe Account"
        subTitle="Set up your payment processing capabilities"
        ghost={false}
        onBack={handleBackToDashboard}
        backIcon={<ArrowLeftOutlined />}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} md={isStripeConnected ? 24 : 16} lg={isStripeConnected ? 24 : 12}>
          {isStripeConnected ? (
            <Card>
              <Result
                status="success"
                icon={<CheckCircleOutlined />}
                title="Your Stripe account is connected!"
                subTitle={`Account ID: ${borrower.stripe_account_id}`}
                extra={[
                  <Button type="primary" key="dashboard" onClick={handleBackToDashboard}>
                    Return to Dashboard
                  </Button>,
                ]}
              />
              <Paragraph style={{ textAlign: 'center' }}>
                You can now receive funds for your projects. Head back to your dashboard to create or manage your
                projects.
              </Paragraph>
            </Card>
          ) : (
            <Card>
              <Title level={4}>Connect Your Stripe Account</Title>
              <Paragraph>
                To receive funding for your projects, you need to connect your account to Stripe. This allows funds to
                be securely transferred to your bank account.
              </Paragraph>

              <StripeOnboarding userType="borrower" userId={borrowerId} email={borrower?.email} />
            </Card>
          )}
        </Col>

        {!isStripeConnected && (
          <Col xs={24} md={8} lg={12}>
            <Card>
              <Title level={4}>Why Connect Stripe?</Title>
              <Space direction="vertical" size="middle">
                <div>
                  <Text strong>Secure Payments</Text>
                  <Paragraph>
                    Stripe is a trusted payment processor that ensures your funds are handled securely.
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Quick Setup</Text>
                  <Paragraph>It only takes a few minutes to connect your account and start receiving funds.</Paragraph>
                </div>

                <div>
                  <Text strong>Direct Deposits</Text>
                  <Paragraph>Funds are deposited directly to your bank account once a project is funded.</Paragraph>
                </div>
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </Main>
  );
}

export default BorrowerStripeConnect;
